import { Box } from '@mui/material'
import Head from 'next/head'
import { useState, useEffect, useCallback } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { useSendPublicProfilePageViewEvent } from '@hooks'
import {
  NavbarV2,
  Footer,
  AboutMe,
  Experience,
  Specialty,
  ContactInfo,
  HeadWithMetaTags,
  Toast,
  ActiveListing,
  Review,
} from 'containers'
import { ProfessionalProfileHeader } from 'containers/professional-profile-header'
import env from 'env'
import { useSelector, getStoreServerSideProps } from 'store'
import {
  actions as agentProfileActions,
  selectAgentProfile,
  selectAgentProfileImage,
  selectSharingMetaProperties,
  selectAgentCoverImage,
  selectAgentProfileHeaderTitle,
} from 'store/agent-profile'
import { actions as agentReviewListActions } from 'store/agent-review-list'
import { actions as agentReviewSummaryActions } from 'store/agent-review-summary'
import { actions as analyticDataActions } from 'store/analytic-data'
import { findOrCreateCookie } from 'utils/cookie'
import { getStatsigInitializeValues, type IGetFeatureFlag } from 'utils/feature-flag/instance'
import { useDispatchFeatureFlagValueByKeys } from 'utils/feature-flag/utils'

type AgentProfilePageProps = {
  isPreview?: boolean
}

const defaultProfileImage = '/profile/default-profile.svg'

const AgentProfilePage: React.FC<AgentProfilePageProps> = ({ isPreview = false }) => {
  const [profileImageSrc, setProfileImageSrc] = useState(defaultProfileImage)
  const [offsetHeightAboutMeShowMore, setOffsetHeightAboutMeShowMore] = useState(0)
  const [offsetHeightExperienceShowMore, setOffsetHeightExperienceShowMore] = useState(0)
  const [offsetHeightSpecialtyShowMore, setOffsetHeightSpecialtyShowMore] = useState(0)

  const image = useSelector(selectAgentProfileImage)
  const coverImage = useSelector(selectAgentCoverImage)
  const headerTitle = useSelector(selectAgentProfileHeaderTitle)
  const metaContents = useSelector(selectSharingMetaProperties)

  useSendPublicProfilePageViewEvent(isPreview)

  useEffect(() => {
    setProfileImageSrc(image)
  }, [image])

  const onShowMoreAboutMeReady = useCallback((v: number) => {
    setOffsetHeightAboutMeShowMore(v)
  }, [])

  const onShowMoreExperienceReady = useCallback((v: number) => {
    setOffsetHeightExperienceShowMore(v)
  }, [])

  const onShowMoreSpecialtyReady = useCallback((v: number) => {
    setOffsetHeightSpecialtyShowMore(v)
  }, [])

  return (
    <>
      <HeadWithMetaTags metaContents={metaContents} title={headerTitle} />
      <Head>
        <link
          href="https://fonts.googleapis.com/css2?family=Poppins:wght@100;200;300;400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
      </Head>
      {!isPreview && <NavbarV2 />}
      <Toast />
      <Box>
        <ProfessionalProfileHeader
          coverImageSrc={coverImage}
          profileImageSrc={profileImageSrc}
          setProfileImageSrc={setProfileImageSrc}
        />
        <Box className="content-container">
          <AboutMe onShowMoreTextReady={onShowMoreAboutMeReady} />
          <ContactInfo isPreview={isPreview} />
          <Experience onShowMoreTextReady={onShowMoreExperienceReady} />
          <Specialty onShowMoreTextReady={onShowMoreSpecialtyReady} />
          <ActiveListing />
          <Review
            isDisplayFooter={!isPreview}
            offsetScroll={
              offsetHeightAboutMeShowMore +
              offsetHeightExperienceShowMore +
              offsetHeightSpecialtyShowMore
            }
          />
          <Footer />
        </Box>
      </Box>
    </>
  )
}

export const getServerSidePropsCB: Parameters<typeof getStoreServerSideProps>[0] =
  store => async context => {
    const {
      params: { slug } = {},
      query: { preview, listingPage, agentReviewPage } = {},
      req,
      res,
    } = context
    const agentProfileSlug: string = (slug || '') as string
    const uid = (
      findOrCreateCookie('_rsp_uid', uuidv4(), {
        req,
        res,
        expires: new Date(Date.UTC(2038, 1, 19, 4, 14, 6, 0)),
        httpOnly: true,
        secure: true,
        domain: env.DOMAIN_NAME,
        sameSite: 'lax',
      }) ?? ''
    ).toString()
    store.dispatch(analyticDataActions.setAnalyticData({ uid }))

    try {
      const response = await store.dispatch(
        agentProfileActions.getAgentProfile({ agentProfileSlug, listingPage }),
      )
      if (response.meta.requestStatus === 'rejected') {
        return { notFound: true }
      }
      const agentProfile = selectAgentProfile(store.getState())
      const { slug: profileSlug, customSlug } = agentProfile
      if (customSlug && profileSlug === agentProfileSlug) {
        return {
          redirect: {
            destination: `/profile/${customSlug}`,
            permanent: false,
          },
        }
      }

      const isPreview = !!preview

      const initializeValues = await getStatsigInitializeValues()

      // TODO: add more feature flag key here
      const featureFlagKeys: IGetFeatureFlag[] = []

      // TODO: need to implement get Statsig flag in this fn when add a new feature flag
      await useDispatchFeatureFlagValueByKeys(store, featureFlagKeys)
      await store.dispatch(
        agentReviewListActions.getAgentReviewList({
          agentProfileSlug,
          page: agentReviewPage,
        }),
      )
      await store.dispatch(agentReviewSummaryActions.getAgentReviewSummary({ agentProfileSlug }))

      return { props: { isPreview, initializeValues } }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
      return { notFound: true }
    }
  }

export const getServerSideProps =
  getStoreServerSideProps<Record<string, unknown>>(getServerSidePropsCB)

export default AgentProfilePage
